/**
 * LoadingProgress — 页面加载进度条
 *
 * 功能：
 * - 顶部细线进度条（类似 YouTube / Medium）
 * - 支持手动控制 / 自动控制
 * - 渐变色 + 光泽动画
 * - 无障碍支持
 *
 * 使用示例：
 * // 在 App 顶部放置
 * <LoadingProgress />
 *
 * // 手动控制
 * const { start, done, setProgress } = useNProgress();
 * start();
 * setProgress(50);
 * done();
 */

import React, { useState, useEffect, useCallback, useRef, memo, createContext, useContext } from 'react';

// ★ Progress Context（全局进度条控制）
const ProgressContext = createContext({
  progress: 0,
  isVisible: false,
  start: () => {},
  done: () => {},
  setProgress: () => {},
});

/**
 * Progress Provider — 在 App 根部提供进度条控制
 */
export const ProgressProvider = memo(({ children }) => {
  const [progress, setProgressState] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setProgressState(10);
    setIsVisible(true);

    // 自动递增到 90%
    intervalRef.current = setInterval(() => {
      setProgressState((prev) => {
        if (prev >= 90) {
          clearTimers();
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
  }, [clearTimers]);

  const done = useCallback(() => {
    clearTimers();
    setProgressState(100);

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setProgressState(0), 300);
    }, 400);
  }, [clearTimers]);

  const setProgress = useCallback((value) => {
    clearTimers();
    setProgressState(Math.min(100, Math.max(0, value)));
  }, [clearTimers]);

  // 清理
  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  return (
    <ProgressContext.Provider value={{ progress, isVisible, start, done, setProgress }}>
      <LoadingProgressBar progress={progress} isVisible={isVisible} />
      {children}
    </ProgressContext.Provider>
  );
});
ProgressProvider.displayName = 'ProgressProvider';

/**
 * Hook: 获取进度条控制函数
 */
export function useNProgress() {
  return useContext(ProgressContext);
}

/**
 * 加载进度条组件
 */
const LoadingProgressBar = memo(({ progress, isVisible }) => {
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-1 transition-opacity duration-300"
      style={{ opacity: isVisible ? 1 : 0 }}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="页面加载进度"
    >
      {/* 背景 */}
      <div className="absolute inset-0 bg-slate-800/50" />
      {/* 进度条 */}
      <div
        className="absolute top-0 left-0 h-full transition-all duration-300 ease-out"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4)',
          boxShadow: '0 0 12px rgba(59, 130, 246, 0.6)',
        }}
      />
      {/* 光泽扫过效果 */}
      <div
        className="absolute top-0 h-full w-20"
        style={{
          left: `${Math.min(progress - 10, 80)}%`,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          animation: isVisible ? 'shimmer 1.5s infinite' : 'none',
        }}
      />
    </div>
  );
});
LoadingProgressBar.displayName = 'LoadingProgressBar';

/**
 * 自动跟踪路由变化的进度条（集成到 App）
 */
export const RouteProgress = memo(() => {
  const { start, done } = useNProgress();

  useEffect(() => {
    start();
    // 路由切换时短暂延迟模拟加载
    const timer = setTimeout(done, 200);
    return () => {
      clearTimeout(timer);
    };
  }, [start, done]);

  return null;
});
RouteProgress.displayName = 'RouteProgress';

/**
 * 独立进度条（不依赖 Context）
 */
export const LoadingProgress = memo(({ progress = 0, className = '' }) => {
  return (
    <div
      className={`w-full h-1 bg-slate-800 rounded-full overflow-hidden ${className}`}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full transition-all duration-300 ease-out"
        style={{
          width: `${Math.min(100, Math.max(0, progress))}%`,
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
        }}
      />
    </div>
  );
});
LoadingProgress.displayName = 'LoadingProgress';

export default LoadingProgress;
